import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useMerchant } from "@auth";
import { getPaymentRequestTemplates } from "@api";
import { useRequestTemplates } from "@flags";

const PaymentRequestTemplatesContext = createContext();

export const usePaymentRequestTemplatesContext = () => {
  const context = useContext(PaymentRequestTemplatesContext);

  if (context === undefined) {
    throw new Error(
      "usePaymentRequestTemplatesContext must be used within a PaymentRequestTemplatesProvider",
    );
  }

  return context;
};

export const PaymentRequestTemplatesProvider = ({ children }) => {
  const { merchant } = useMerchant();
  const enabled = useRequestTemplates();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!enabled || !merchant?.id) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getPaymentRequestTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch payment request templates:", error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, merchant?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const templatesById = useMemo(() => {
    const map = new Map();
    for (const t of templates) map.set(t.id, t);
    return map;
  }, [templates]);

  const getTemplateById = useCallback(
    (id) => templatesById.get(id),
    [templatesById],
  );

  const getTemplateNameById = useCallback(
    (id) => templatesById.get(id)?.name,
    [templatesById],
  );

  const refresh = useCallback(() => fetchData(), [fetchData]);

  const value = {
    templates,
    loading,
    enabled,
    getTemplateById,
    getTemplateNameById,
    refresh,
  };

  return (
    <PaymentRequestTemplatesContext.Provider value={value}>
      {children}
    </PaymentRequestTemplatesContext.Provider>
  );
};
